import { FbLink } from 'components/FbUI/FbLink'
import React, { useEffect } from 'react'
import { FiUpload } from 'react-icons/fi'

import FileContainer from 'components/FileContainer/FileContainer'
import { IoMdCheckmarkCircleOutline } from 'react-icons/io'

export interface FileDragDropProps {
  onFileChange: (file: File | null) => void
  file: File | null
  errorMessage?: string
  supportText?: string
}
export function FileDragDrop({
  onFileChange,
  file,
  errorMessage,
  supportText,
}: FileDragDropProps) {
  const [dragActive, setDragActive] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const handleDrag = (
    e: React.DragEvent<HTMLFormElement> | React.DragEvent<HTMLDivElement>
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileChange(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      onFileChange(e.target.files[0])
    }
  }

  // triggers the input when the button is clicked
  const onButtonClick = () => {
    inputRef.current?.click()
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current!.value = ''
    }
  }, [])

  return (
    <form
      className="relative"
      onDragEnter={handleDrag}
      onSubmit={(e) => e.preventDefault()}
    >
      <input
        ref={inputRef}
        type="file"
        id="input-file-upload"
        multiple={false}
        className="hidden"
        onChange={handleChange}
      />
      <label
        htmlFor="input-file-upload"
        className={`
          flex-1 flex flex-col items-center py-10 rounded-lg cursor-pointer
          ${dragActive ? 'bg-slate-50' : ''}
        `}
        onClick={(e: React.MouseEvent<HTMLLabelElement, MouseEvent>) =>
          e.preventDefault()
        }
      >
        <FiUpload size={40} color="#798AA3" />
        {file ? (
          <WithFiles file={file} onFileChange={onFileChange} />
        ) : (
          <NoFiles
            onButtonClick={onButtonClick}
            supportText={supportText ?? ''}
          />
        )}
      </label>
      {dragActive && (
        <div
          className="absolute inset-0 w-full h-full rounded-2xl"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        />
      )}
      {errorMessage && (
        <div className="mt-2 text-sm text-red-600">
          {errorMessage.replace('Error: ', '')}
        </div>
      )}
    </form>
  )
}

function NoFiles({
  onButtonClick,
  supportText,
}: {
  onButtonClick: () => void
  supportText: string
}) {
  return (
    <>
      <div className="mt-5 text-base font-semibold text-slate-800">
        Drag & drop file here
      </div>
      <div className="text-sm font-normal text-slate-800">
        or{' '}
        <FbLink to="#" onClick={onButtonClick}>
          browse
        </FbLink>{' '}
        to choose a file
      </div>
      <div className="mt-4 text-xs font-normal text-slate-600 whitespace-pre-line">
        {supportText}
      </div>
    </>
  )
}

function WithFiles({
  file,
  onFileChange,
}: {
  file: File
  onFileChange: (file: File | null) => void
}) {
  const handleRemoveFile = () => {
    onFileChange(null)
  }

  return (
    <>
      <div className="mt-5 text-base font-semibold text-slate-800">
        Upload - Success!
      </div>
      <FileContainer
        LeftIcon={IoMdCheckmarkCircleOutline}
        file={file}
        handleRemoveFile={handleRemoveFile}
      />
    </>
  )
}
